import React, { useState } from 'react';
import { changePassword } from '../utils/authService';
import Header from '../components/Header';

const styles = {
  page: {
    minHeight: '100vh',
    background: 'var(--color-bg)',
  },
  content: {
    maxWidth: 520,
    margin: '0 auto',
    padding: '64px 24px 48px',
  },
  card: {
    background: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: 12,
    padding: 32,
  },
  title: {
    fontFamily: 'var(--font-heading)',
    fontSize: 22,
    fontWeight: 700,
    color: 'var(--color-secondary)',
    letterSpacing: 2,
    textTransform: 'uppercase',
    marginBottom: 8,
    textAlign: 'center',
  },
  subtitle: {
    fontFamily: 'var(--font-body)',
    fontSize: 14,
    color: 'var(--color-text-dim)',
    lineHeight: 1.6,
    textAlign: 'center',
    marginBottom: 28,
  },
  formGroup: {
    marginBottom: 18,
  },
  label: {
    display: 'block',
    fontFamily: 'var(--font-heading)',
    fontSize: 10,
    color: 'var(--color-text-dim)',
    letterSpacing: 1.5,
    textTransform: 'uppercase',
    marginBottom: 8,
  },
  input: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '11px 13px',
    background: 'var(--color-bg)',
    border: '1px solid var(--color-border)',
    borderRadius: 8,
    color: 'var(--color-text)',
    fontFamily: 'var(--font-body)',
    fontSize: 14,
    outline: 'none',
  },
  button: {
    width: '100%',
    padding: '12px 18px',
    background: 'var(--color-primary)',
    border: 'none',
    borderRadius: 8,
    color: '#fff',
    fontFamily: 'var(--font-heading)',
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 1.5,
    textTransform: 'uppercase',
    cursor: 'pointer',
    marginTop: 8,
  },
  success: {
    marginTop: 16,
    padding: '10px 12px',
    background: 'rgba(39, 174, 96, 0.12)',
    border: '1px solid rgba(39, 174, 96, 0.24)',
    borderRadius: 8,
    color: '#27ae60',
    fontFamily: 'var(--font-body)',
    fontSize: 13,
  },
  error: {
    marginTop: 16,
    padding: '10px 12px',
    background: 'rgba(231, 76, 60, 0.12)',
    border: '1px solid rgba(231, 76, 60, 0.24)',
    borderRadius: 8,
    color: '#e74c3c',
    fontFamily: 'var(--font-body)',
    fontSize: 13,
  },
};

export default function ResetPasswordPage({ user, onLogout, onPasswordChanged }) {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!newPassword || !confirmPassword) {
      setError('Please complete both password fields.');
      return;
    }
    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setSaving(true);
    try {
      await changePassword('', newPassword);
      if (onPasswordChanged) onPasswordChanged();
    } catch (err) {
      setError(err.message || 'Failed to update password.');
      setSaving(false);
    }
  };

  return (
    <div style={styles.page} data-doc-id="reset-password-page">
      <Header user={user} onLogout={onLogout} />
      <div style={styles.content}>
        <form style={styles.card} onSubmit={handleSubmit} data-doc-id="reset-password-form">
          <div style={styles.title}>Set Your New Password</div>
          <div style={styles.subtitle}>
            You signed in with a temporary password. Please choose a new password below to continue to RaceVoice.
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>New Password</label>
            <input
              data-doc-id="reset-new-password"
              style={styles.input}
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              autoFocus
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Confirm New Password</label>
            <input
              data-doc-id="reset-confirm-password"
              style={styles.input}
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
            />
          </div>

          <button
            type="submit"
            style={{ ...styles.button, opacity: saving ? 0.6 : 1 }}
            disabled={saving}
            data-doc-id="reset-submit"
          >
            {saving ? 'Updating...' : 'Set New Password'}
          </button>

          {error && <div style={styles.error}>{error}</div>}
        </form>
      </div>
    </div>
  );
}
